import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState, ErrorState, FormDialog } from '@ui-kit';
import { useMatrix } from '../hooks/useMatrix';

interface PermissionMatrixTabProps {
  enabled: boolean;
}

export function PermissionMatrixTab({ enabled }: PermissionMatrixTabProps) {
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useMatrix(enabled);

  // Skeleton while loading
  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="매트릭스 로딩 중">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              height: 20,
              width: '30%',
              borderRadius: 'var(--radius-sm)',
              background: 'color-mix(in srgb, var(--color-text-muted) 20%, transparent)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </div>
        <div
          style={{
            overflowX: 'auto',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[1, 2, 3, 4, 5].map((i) => (
                  <th key={i} style={{ padding: 'var(--spacing-3)' }}>
                    <div
                      aria-hidden="true"
                      style={{
                        height: 14,
                        width: 60,
                        borderRadius: 'var(--radius-sm)',
                        background: 'color-mix(in srgb, var(--color-text-muted) 20%, transparent)',
                        animation: 'pulse 1.5s ease-in-out infinite',
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4].map((row) => (
                <tr key={row}>
                  {[1, 2, 3, 4, 5].map((col) => (
                    <td key={col} style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
                      <div
                        aria-hidden="true"
                        style={{
                          height: 24,
                          width: 24,
                          margin: '0 auto',
                          borderRadius: 'var(--radius-sm)',
                          background: 'color-mix(in srgb, var(--color-text-muted) 20%, transparent)',
                          animation: 'pulse 1.5s ease-in-out infinite',
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="매트릭스 로드 실패"
        message={error?.message ?? '잠시 후 다시 시도해주세요.'}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data || (data.roles.length === 0 && data.permissions.length === 0)) {
    return (
      <EmptyState
        title="정의된 역할/권한이 없습니다"
        description="role-management 에서 역할과 권한을 먼저 생성해 주세요."
        action={
          <button
            type="button"
            onClick={() => navigate('/roles')}
            style={{
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              padding: 'var(--spacing-2) var(--spacing-4)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            역할 관리로 이동
          </button>
        }
      />
    );
  }

  // Build a lookup set for O(1) grant checks
  const grantedSet = new Set<string>(
    data.cells.filter((c) => c.granted).map((c) => `${c.role}::${c.permission}`)
  );
  const systemRoleSet = new Set<string>(
    data.cells.filter((c) => c.systemRole).map((c) => c.role)
  );

  return (
    <div>
      {/* Header */}
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-base)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
              }}
            >
              역할 × 권한 매트릭스
            </h3>
            <p
              style={{
                margin: 'var(--spacing-1) 0 0',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              시스템에 정의된 모든 역할과 권한의 부여 현황
            </p>
          </div>

          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                fontStyle: 'italic',
              }}
            >
              읽기 전용 — 편집은 [역할 관리] 에서
            </span>
            <button
              type="button"
              data-testid="matrix-goto-roles-btn"
              onClick={() => setNavDialogOpen(true)}
              style={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                color: 'var(--color-text-secondary)',
                padding: 'var(--spacing-1) var(--spacing-3)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
              aria-label="역할 관리 페이지로 이동"
            >
              역할 관리로 이동
            </button>
          </div>
        </div>

        {/* Matrix table */}
        <div style={{ overflowX: 'auto' }}>
          <table
            role="grid"
            aria-label="역할 × 권한 매트릭스 (읽기 전용)"
            aria-readonly="true"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-background)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              overflow: 'hidden',
            }}
          >
            <thead>
              <tr>
                {/* Role \ Permission header cell */}
                <th
                  scope="col"
                  style={{
                    padding: 'var(--spacing-3)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 700,
                    fontSize: 'var(--font-size-xs)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'left',
                    minWidth: 140,
                    borderBottom: '1px solid var(--color-surface-border)',
                    borderRight: '1px solid var(--color-surface-border)',
                  }}
                >
                  역할 \ 권한
                </th>
                {data.permissions.map((perm) => (
                  <th
                    key={perm}
                    scope="col"
                    style={{
                      padding: 'var(--spacing-3)',
                      background: 'var(--color-surface-elevated)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 700,
                      fontSize: 'var(--font-size-xs)',
                      textAlign: 'center',
                      borderBottom: '1px solid var(--color-surface-border)',
                      borderRight: '1px solid var(--color-surface-border)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <code style={{ fontFamily: 'var(--font-family-mono)' }}>{perm}</code>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.roles.map((role, rIdx) => {
                const isSystem = systemRoleSet.has(role);
                return (
                  <tr key={role}>
                    <th
                      scope="row"
                      style={{
                        padding: 'var(--spacing-3)',
                        textAlign: 'left',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 700,
                        color: 'var(--color-text-primary)',
                        background: 'var(--color-surface-muted)',
                        borderBottom: rIdx < data.roles.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                        borderRight: '1px solid var(--color-surface-border)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                        {isSystem && (
                          <span
                            aria-label="시스템 역할 (보호됨)"
                            title="시스템 역할 (보호됨)"
                            style={{ fontSize: 12, flexShrink: 0 }}
                          >
                            🔒
                          </span>
                        )}
                        {role}
                      </div>
                    </th>
                    {data.permissions.map((perm) => {
                      const granted = grantedSet.has(`${role}::${perm}`);
                      return (
                        <td
                          key={perm}
                          role="gridcell"
                          aria-label={`${role} × ${perm}: ${granted ? '부여됨' : '미부여'}`}
                          style={{
                            padding: 'var(--spacing-3)',
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            borderBottom: rIdx < data.roles.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                            borderRight: '1px solid var(--color-surface-border)',
                          }}
                        >
                          {granted ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24,
                                borderRadius: 'var(--radius-sm)',
                                background: 'var(--color-brand-primary)',
                                color: 'var(--color-text-inverse)',
                                fontWeight: 700,
                                fontSize: 'var(--font-size-xs)',
                              }}
                              aria-hidden="true"
                            >
                              ✓
                            </span>
                          ) : (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24,
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--color-surface-border)',
                                background: 'var(--color-surface-elevated)',
                              }}
                              aria-hidden="true"
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-3)',
            background: 'var(--color-surface-elevated)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            marginTop: 'var(--spacing-3)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-brand-primary)',
                display: 'inline-block',
              }}
            />
            부여됨
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-surface-border)',
                background: 'var(--color-surface-elevated)',
                display: 'inline-block',
              }}
            />
            미부여
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span style={{ fontSize: 12 }}>🔒</span>
            시스템 역할 (보호됨)
          </span>
        </div>
      </div>

      {/* Navigation dialog (dialog state) */}
      <FormDialog
        open={navDialogOpen}
        title="역할 관리 페이지로 이동"
        onClose={() => setNavDialogOpen(false)}
        onSubmit={() => {
          setNavDialogOpen(false);
          navigate('/roles');
        }}
        submitLabel="이동"
        cancelLabel="취소"
        submitTestId="matrix-nav-dialog-submit"
      >
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
          이 페이지의 매트릭스는 읽기 전용입니다. 역할-권한 매핑을 편집하려면 역할 관리 페이지로 이동하시겠습니까?
        </p>
      </FormDialog>
    </div>
  );
}
